import { getToken, handleSessionExpiry, isTokenExpired } from "@/utils/auth";

export const fetcher = async (
    url: string,
    method: string = "GET",
    headers: Record<string, string> = {},
    body: BodyInit | null = null,
    returnBlob: boolean = false,
    // The auth handshake (AuthWrapper's /user_info check, LoginPage's /auth) must
    // opt out: those calls ARE the re-auth path, so redirecting on their 401 would
    // loop or wipe the login form. Everything else defaults to auto re-auth.
    options: { redirectOnUnauthorized?: boolean } = {}
) => {
    const { redirectOnUnauthorized = true } = options;
    const jwtToken = getToken();

    // Proactive expiry check (ES-1030): if we hold a token that has already
    // expired, don't fire a request we know will 401 — go straight to re-auth.
    if (redirectOnUnauthorized && jwtToken && isTokenExpired(jwtToken)) {
        handleSessionExpiry();
        throw new Error("Session expired");
    }

    let res = await fetch(url, {
        headers: {
            Authorization: `Bearer ${jwtToken}`,
            ...headers,
        },
        method,
        body,
    });

    if (res.status === 500) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        res = await fetch(url, {
            headers: {
                Authorization: `Bearer ${jwtToken}`,
                ...headers,
            },
            method,
            body,
        });
    }

    if (res.status === 500) {
        throw new Error("An error occurred while fetching the data.");
    }

    // Session expired / token rejected server-side (ES-1030): a token was sent but
    // the server refused it. Clear the session and reload into the login screen
    // instead of surfacing a generic error that pages swallow as "No data available".
    if (redirectOnUnauthorized && jwtToken && res.status === 401) {
        handleSessionExpiry();
    }

    if (!res.ok) {
        let errorInfo;
        try {
            errorInfo = await res.json();
        } catch (e) {
            console.error("Error parsing JSON:", e);
        }
        throw new Error("An error occurred while fetching the data.", {
            cause: { ...errorInfo, status: res.status },
        });
    }

    if (returnBlob) {
        return res.blob();
    }

    return res.json();
};
